import { moduleFieldsOf } from "@/lib/geo";

export function pointInPoly([x, y], poly) {
  let r = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) r = !r;
  }
  return r;
}

// Operazioni sui campi moduli di una falda: adatta, copia, rimuovi, sposta, ridimensiona a moduli interi.
// I poligoni sono in lat/lng; f = campo del layout (frame, polyUV, su, sv, du, dv, gu, cells).

const bboxUV = (pts) => [Math.min(...pts.map((q) => q[0])), Math.max(...pts.map((q) => q[0])), Math.min(...pts.map((q) => q[1])), Math.max(...pts.map((q) => q[1]))];
const rectLL = (f, [U0, U1, V0, V1]) => [[U0, V0], [U1, V0], [U1, V1], [U0, V1]].map(f.frame.toLL);
const inside = (f, ptsLL) => ptsLL.every((ll) => { const q = f.frame.toUV(ll); return pointInPoly(q, f.polyUV) || f.polyUV.some((p) => Math.hypot(p[0] - q[0], p[1] - q[1]) < 1e-3); });
const segDist = (p, a, b) => {
  const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2));
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
};
// dentro il tetto e ad almeno "Dai bordi" dal contorno (stessa regola della posa)
const insideEdge = (f, ptsLL) => ptsLL.every((ll) => {
  const q = f.frame.toUV(ll), P = f.polyUV;
  return pointInPoly(q, P) && P.every((a, k) => segDist(q, a, P[(k + 1) % P.length]) >= (f.edge || 0) - 1e-6);
});
const keyOf = (i) => (k) => (i ? k.startsWith(`a${i}:`) : !k.includes(":"));

// sostituisce i campi della falda; i moduli tolti a mano e i ganci manuali dei campi toccati vengono azzerati
export function setFields(roof, fields, touched = null) {
  const drop = touched === null ? () => true : keyOf(touched);
  const hook = Object.fromEntries(Object.entries(roof.hook_pos || {}).filter(([k]) => !drop(k)));
  return { ...roof, module_fields: fields, module_area: null, fields_mode: true, removed: (roof.removed || []).filter((k) => !drop(k)), hook_pos: hook };
}

export const fieldsOf = moduleFieldsOf;

// rimozione del campo i: le chiavi dei campi successivi scalano di uno (a{j}: → a{j-1}:), così i moduli tolti a mano restano
export function dropField(roof, fields, i) {
  const idx = (k) => { const m = /^a(\d+):(.*)$/.exec(k); return m ? [Number(m[1]), m[2]] : [0, k]; };
  const remap = (k) => { const [j, rest] = idx(k); return j === i ? null : j > i ? (j - 1 ? `a${j - 1}:${rest}` : rest) : k; };
  const keep = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [remap(k), v]).filter(([k]) => k));
  return { ...roof, module_fields: fields.filter((_, j) => j !== i), module_area: null, fields_mode: true,
    removed: (roof.removed || []).map(remap).filter(Boolean), hook_pos: keep(roof.hook_pos) };
}

// campi modificabili: con "Intero tetto" il contorno del tetto vale come primo campo (diventa reale alla prima modifica)
export const editableFields = (roof) => { const f = moduleFieldsOf(roof); return f.length ? f : roof.fields_mode ? [] : [roof.polygon]; };

// Adatta automaticamente le dimensioni del campo: il contorno si stringe sui moduli posati
export function fitField(f, i) {
  const cells = f.cells.filter((c) => (c.a || 0) === i && c.active);
  if (!cells.length) return null;
  const b = [Math.min(...cells.map((c) => c.rect[0])), Math.max(...cells.map((c) => c.rect[2])), Math.min(...cells.map((c) => c.rect[1])), Math.max(...cells.map((c) => c.rect[3]))];
  return rectLL(f, b);
}

// traslazione (m nel riferimento della falda) limitata in modo che il campo resti dentro il tetto
export function moveField(f, poly, dU, dV) {
  const tr = (t) => poly.map((ll) => { const q = f.frame.toUV(ll); return f.frame.toLL([q[0] + dU * t, q[1] + dV * t]); });
  const ok = insideEdge(f, poly) ? insideEdge : inside;
  if (!inside(f, poly) || ok(f, tr(1))) return tr(1);
  let lo = 0, hi = 1;
  for (let k = 0; k < 20; k++) { const m = (lo + hi) / 2; if (ok(f, tr(m))) lo = m; else hi = m; }
  return tr(lo);
}

// copia nel primo posto libero accanto (lungo la gronda, poi sopra/sotto, sempre più lontano); se non c'è posto resta sovrapposta e spostata di poco
export function copyField(f, poly, others = []) {
  const [U0, U1, V0, V1] = bboxUV(poly.map(f.frame.toUV));
  const gv = f.sv - f.dv;
  const boxes = others.map((o) => bboxUV(o.map(f.frame.toUV)));
  const hitsOther = (b) => boxes.some((o) => b[0] < o[1] - 1e-6 && o[0] < b[1] - 1e-6 && b[2] < o[3] - 1e-6 && o[2] < b[3] - 1e-6);
  const wU = U1 - U0 + f.gu, wV = V1 - V0 + gv, steps = [];
  for (let k = 1; k <= 30; k++) steps.push([k * wU, 0], [-k * wU, 0], [0, k * wV], [0, -k * wV]);
  for (const [dU, dV] of steps) {
    const cand = poly.map((ll) => { const q = f.frame.toUV(ll); return f.frame.toLL([q[0] + dU, q[1] + dV]); });
    if (insideEdge(f, cand) && !hitsOther([U0 + dU, U1 + dU, V0 + dV, V1 + dV])) return { poly: cand, free: true };
  }
  return { poly: poly.map((ll) => { const q = f.frame.toUV(ll); return f.frame.toLL([q[0] + 0.5, q[1] + 0.5]); }), free: false };
}

// ridimensionamento a moduli interi da una maniglia di lato ("l","r","b","t"): ritorna poligono e n° colonne × righe
export function resizeField(f, poly, side, ptUV, i = 0) {
  let [U0, U1, V0, V1] = bboxUV(poly.map(f.frame.toUV));
  // campo non rettangolare o a filo del bordo (es. intero tetto): si parte dall'ingombro dei moduli posati
  const fit = !insideEdge(f, poly) && fitField(f, i);
  if (fit) [U0, U1, V0, V1] = bboxUV(fit.map(f.frame.toUV));
  const nU = (len) => Math.max(1, Math.floor((len + f.gu) / f.su + 1e-6)), nV = (len) => Math.max(1, Math.floor((len + f.sv - f.dv) / f.sv + 1e-6));
  const lenU = (n) => n * f.su - f.gu, lenV = (n) => n * f.sv - (f.sv - f.dv);
  // l'altro asse viene portato a moduli interi (per difetto), così l'etichetta coincide con i moduli posati
  if (side === "r" || side === "l") V1 = V0 + lenV(nV(V1 - V0)); else U1 = U0 + lenU(nU(U1 - U0));
  const box = (n) => {
    const b = [U0, U1, V0, V1];
    if (side === "r") b[1] = U0 + lenU(n);
    if (side === "l") b[0] = U1 - lenU(n);
    if (side === "t") b[3] = V0 + lenV(n);
    if (side === "b") b[2] = V1 - lenV(n);
    return b;
  };
  const along = side === "r" ? ptUV[0] - U0 : side === "l" ? U1 - ptUV[0] : side === "t" ? ptUV[1] - V0 : V1 - ptUV[1];
  let n = side === "r" || side === "l" ? Math.max(1, Math.round((along + f.gu) / f.su)) : Math.max(1, Math.round((along + f.sv - f.dv) / f.sv));
  // non oltre il tetto: si riduce n finché il rettangolo resta dentro (se il campo era già dentro)
  const wasIn = !!fit || insideEdge(f, poly);
  while (wasIn && n > 1 && !insideEdge(f, rectLL(f, box(n)))) n--;
  const [a0, a1, b0, b1] = box(n);
  return { poly: rectLL(f, [a0, a1, b0, b1]), cols: nU(a1 - a0), rows: nV(b1 - b0) };
}

export const fieldBox = (f, poly) => bboxUV(poly.map(f.frame.toUV));
